import {
  AxiosError,
  type GpuHostOperationsView,
  getGpuHostOperations,
  queryKeys,
  useAuthStore,
} from "@dutyon/apis";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  formatGpuBytes,
  formatGpuDuration,
  formatGpuTime,
  gpuFailures,
  gpuHostPresentation,
  gpuJobStages,
  gpuServerFinalization,
  gpuStoragePresentation,
} from "./model";

export function GpuHostPage() {
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  if (!token || user?.role !== "SUPER_ADMIN")
    return <Alert color="red">최고 관리자만 GPU 운영 상태를 조회할 수 있습니다.</Alert>;
  return <GpuHostOperations key={user.sub} owner={user.sub} />;
}
function GpuHostOperations({ owner }: { owner: string }) {
  const [visible, setVisible] = useState(document.visibilityState === "visible");
  const [elapsed, setElapsed] = useState(0);
  const status = useQuery({
    queryKey: queryKeys.gpuHost.operations(owner),
    queryFn: ({ signal }) => getGpuHostOperations(owner, signal),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchInterval: (query) => {
      const error = query.state.error;
      if (error instanceof AxiosError && [401, 403].includes(error.response?.status ?? 0))
        return false;
      return visible ? 5000 : 30000;
    },
    refetchIntervalInBackground: true,
  });
  useEffect(() => {
    const change = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", change);
    return () => document.removeEventListener("visibilitychange", change);
  }, []);
  useEffect(() => {
    if (!status.dataUpdatedAt) return;
    const started = performance.now();
    setElapsed(0);
    const timer = setInterval(() => setElapsed(performance.now() - started), 1000);
    return () => clearInterval(timer);
  }, [status.dataUpdatedAt]);
  return (
    <TitleContainer title="GPU 운영 상태">
      <Stack>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Mac이 보내온 상태와 클라우드 수신 기록입니다. 이 화면에서 작업이나 서비스를 제어하지
            않습니다.
          </Text>
          <Button variant="light" loading={status.isFetching} onClick={() => void status.refetch()}>
            상태 새로고침
          </Button>
        </Group>
        {status.isPending && <Text role="status">상태를 불러오는 중입니다.</Text>}
        {status.isError && (
          <Alert color="orange" title="운영 상태 조회 실패">
            현재 상태를 확인하지 못했습니다. 권한과 API 연결을 확인한 후 다시 조회해 주세요.
          </Alert>
        )}
        {!status.isError && status.data && <GpuHostSnapshot view={status.data} elapsed={elapsed} />}
      </Stack>
    </TitleContainer>
  );
}
function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group justify="space-between" align="start">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" style={{ overflowWrap: "anywhere", textAlign: "right" }}>
        {children}
      </Text>
    </Group>
  );
}
function GpuHostSnapshot({ view, elapsed }: { view: GpuHostOperationsView; elapsed: number }) {
  const state = gpuHostPresentation(view, elapsed);
  const snapshot = view.host?.observation;
  const now = state.now;
  return (
    <Stack>
      <Card withBorder>
        <Group justify="space-between">
          <Text fw={700}>서비스 상태</Text>
          <Badge color={state.color} size="lg">
            {state.label}
          </Badge>
        </Group>
        <Text size="sm" mt="sm">
          {!view.configured
            ? "서버의 GPU 상태 수집 설정이 아직 활성화되지 않았습니다."
            : !snapshot
              ? "등록 장비의 첫 보고가 도착하면 상태를 표시합니다."
              : state.stale
                ? "마지막 관측값입니다. Mac 중지나 회선 단절 여부를 확인해 주세요. 시간 경과만으로 작업 실패를 확정하지 않습니다."
                : "클라우드에 수신된 최신 관측입니다. GPU 준비와 외부 연결을 각각 확인해 주세요."}
        </Text>
      </Card>
      <Card withBorder>
        <Detail label="API → GPU 마지막 접속 성공">
          {formatGpuTime(view.externalReachability.lastVerifiedAt)}
        </Detail>
        <Text size="xs" c="dimmed" mt="xs">
          서버가 검증한 마지막 응답 시각입니다. 현재 연결 가능 여부나 GPU 준비 상태를 보장하지
          않습니다.
        </Text>
      </Card>
      {snapshot && (
        <>
          {state.stale && (
            <Alert color="orange" title="마지막 관측값">
              아래 정보는 현재 상태로 보장되지 않습니다. 마지막 수신:{" "}
              {formatGpuTime(view.host?.receivedAt)}
            </Alert>
          )}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Card withBorder>
              <Stack gap="xs">
                <Text fw={700}>연결과 구성 요소</Text>
                <Detail label="장비 · 환경">
                  {snapshot.hostId} · {snapshot.environment}
                </Detail>
                <Detail label="GPU 준비">
                  {snapshot.gpu.ready ? "준비 완료" : "준비 안 됨"}
                  {state.stale ? " (마지막 관측)" : ""}
                </Detail>
                <Detail label="로컬 큐">
                  {snapshot.queue.started && snapshot.queue.healthy ? "정상" : "확인 필요"}
                </Detail>
                <Detail label="인증 기록 정리">
                  {snapshot.nonceMaintenance.started && snapshot.nonceMaintenance.healthy
                    ? "정상"
                    : "확인 필요"}
                </Detail>
                <Detail label="로컬 Tunnel 연결 수">
                  {snapshot.tunnel.state === "metrics"
                    ? `${snapshot.tunnel.connections}개 · ${formatGpuTime(snapshot.tunnel.checkedAt)}`
                    : "확인 불가"}
                </Detail>
                <Detail label="API → GPU 접속 성공">
                  {formatGpuTime(view.externalReachability.lastVerifiedAt)}
                </Detail>
                <Detail label="Mac 관측 시각">{formatGpuTime(snapshot.observedAt)}</Detail>
                <Detail label="클라우드 수신 시각">{formatGpuTime(view.host?.receivedAt)}</Detail>
                <Detail label="Metal 준비 확인">
                  {formatGpuTime(snapshot.gpu.probeSucceededAt)}
                </Detail>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text fw={700}>작업과 유지보수</Text>
                <Text size="sm" c="dimmed">
                  {snapshot.jobs.complete ? "로컬 보관 원장 전체 집계" : "로컬 원장 부분 집계"} ·{" "}
                  {snapshot.jobs.examined}건 검사. 기간별 처리량이 아닙니다.
                </Text>
                <Detail label="대기 · 실행">
                  {snapshot.jobs.queued} · {snapshot.jobs.running}
                </Detail>
                <Detail label="새 작업 접수 여유">
                  {!snapshot.queueAdmission
                    ? "제공 안 함"
                    : snapshot.queueAdmission.reason === "measurement_failed"
                      ? "집계 실패 · 새 접수 차단"
                      : `${snapshot.queueAdmission.liveJobs} / ${snapshot.queueAdmission.maximumLiveJobs}건 · ${snapshot.queueAdmission.ready ? "여유 있음" : "한도 도달"}`}
                </Detail>
                <Detail label="접수 한도 확인 시각">
                  {formatGpuTime(snapshot.queueAdmission?.checkedAt)}
                </Detail>
                <Detail label="결과 보관 · 실패">
                  {snapshot.jobs.succeeded} · {snapshot.jobs.failed}
                </Detail>
                <Detail label="취소 · 시간 초과">
                  {snapshot.jobs.cancelled} · {snapshot.jobs.timedOut}
                </Detail>
                <Detail label="가장 오래 기다린 시간">
                  {snapshot.jobs.oldestQueuedAt === null
                    ? "대기 기록 없음"
                    : formatGpuDuration(snapshot.observedAt - snapshot.jobs.oldestQueuedAt)}
                </Detail>
                <Detail label="유지보수 성공">
                  {formatGpuTime(snapshot.cloudMaintenance?.lastSuccessAt)}
                </Detail>
                <Detail label="유지보수 오류">
                  {snapshot.cloudMaintenance?.lastError ??
                    (snapshot.cloudMaintenance ? "없음" : "설정 안 됨")}
                </Detail>
                <Detail label="상태 전송 성공">
                  {formatGpuTime(snapshot.heartbeat?.lastSuccessAt)}
                </Detail>
                <Detail label="상태 전송 오류">
                  {snapshot.heartbeat?.lastError ?? (snapshot.heartbeat ? "없음" : "제공 안 함")}
                </Detail>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text fw={700}>장비와 엔진</Text>
                <Detail label="칩">{snapshot.device.chip}</Detail>
                <Detail label="운영체제 · 구조">
                  {snapshot.device.platform} {snapshot.device.osVersion} ·{" "}
                  {snapshot.device.architecture}
                </Detail>
                <Detail label="엔진 · 규칙">
                  {snapshot.gpu.engineVersion} · {snapshot.gpu.rulesVersion}
                </Detail>
                <Detail label="가동 시간">{formatGpuDuration(snapshot.uptimeMs)}</Detail>
                <Detail label="전체 · 여유 메모리">
                  {formatGpuBytes(snapshot.device.totalMemoryBytes)} ·{" "}
                  {formatGpuBytes(snapshot.device.freeMemoryBytes)}
                </Detail>
                <Detail label="호스트 메모리">
                  {formatGpuBytes(snapshot.device.processMemoryBytes)}
                </Detail>
                <Detail label="디스크 여유">{formatGpuBytes(snapshot.device.diskFreeBytes)}</Detail>
                <Detail label="GPU 사용률 · 온도">측정 불가</Detail>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text fw={700}>디스크와 결과 정리</Text>
                <Detail label="디스크 접수 기준">{gpuStoragePresentation(snapshot).disk}</Detail>
                <Detail label="최소 여유 공간">
                  {snapshot.diskAdmission
                    ? formatGpuBytes(snapshot.diskAdmission.minimumFreeBytes)
                    : "설정/관측 없음"}
                </Detail>
                <Detail label="디스크 측정 시각">
                  {formatGpuTime(snapshot.diskAdmission?.checkedAt)}
                </Detail>
                <Detail label="결과 자동 정리">{gpuStoragePresentation(snapshot).retention}</Detail>
                <Detail label="마지막 정리 성공">
                  {formatGpuTime(snapshot.retention?.lastSuccessAt)}
                </Detail>
                <Detail label="마지막 정리 삭제 수">
                  {snapshot.retention?.lastRemoved ?? "제공 안 함"}
                </Detail>
                <Detail label="사용 중이라 건너뛴 횟수">
                  {snapshot.retention?.skippedPasses ?? "제공 안 함"}
                </Detail>
                <Text size="sm" c="dimmed">
                  마지막 관측 기준입니다. 공간 기준 충족은 다른 준비 조건이나 외부 연결의 정상을
                  보장하지 않습니다. 삭제 수는 최근 한 번의 정리 결과입니다.
                </Text>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text fw={700}>진단 안내</Text>
                {state.stale && (
                  <Text size="sm">
                    연결 확인 불가: Mac 전원·절전·네트워크와 상태 전송 설정을 확인해 주세요.
                  </Text>
                )}
                {!state.stale && !snapshot.gpu.ready && (
                  <Text size="sm">
                    GPU 준비 안 됨: 로컬 서비스의 Metal 준비 검사와 큐 상태를 확인해 주세요.
                  </Text>
                )}
                <Text size="sm">
                  서버 확정은 클라우드의 결과·실패 저장과 해당 사용량 정산 확인입니다. 업무 근무표
                  적용 여부는 해당 업무 화면에서 확인해 주세요.
                </Text>
              </Stack>
            </Card>
          </SimpleGrid>
          <Card withBorder>
            <Text fw={700} mb="sm">
              최근 로컬 작업 · 최대 20건
            </Text>
            {snapshot.jobs.recent.length === 0 ? (
              <Text size="sm">관측된 작업이 없습니다.</Text>
            ) : (
              <Table.ScrollContainer minWidth={800}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>작업 · 앱</Table.Th>
                      <Table.Th>단계</Table.Th>
                      <Table.Th>시도</Table.Th>
                      <Table.Th>경과</Table.Th>
                      <Table.Th>마감</Table.Th>
                      <Table.Th>실패 사유</Table.Th>
                      <Table.Th>서버 확정</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {snapshot.jobs.recent.map((job) => (
                      <Table.Tr key={`${job.app}:${job.jobId}:${job.attempt}`}>
                        <Table.Td>
                          {job.jobId}
                          <Text size="xs" c="dimmed">
                            {job.app} · {snapshot.environment}
                          </Text>
                        </Table.Td>
                        <Table.Td>{gpuJobStages[job.stage]}</Table.Td>
                        <Table.Td>{job.attempt}</Table.Td>
                        <Table.Td>
                          {formatGpuDuration(
                            (job.stage === "queued" || job.stage === "running"
                              ? state.stale
                                ? snapshot.observedAt
                                : now
                              : job.updatedAt) - (job.startedAt ?? job.acceptedAt),
                          )}
                          {state.stale ? " (관측 당시)" : ""}
                        </Table.Td>
                        <Table.Td>{formatGpuTime(job.deadlineAt)}</Table.Td>
                        <Table.Td>{job.failure ? gpuFailures[job.failure] : "없음"}</Table.Td>
                        <Table.Td>{gpuServerFinalization(view, job)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>
        </>
      )}
    </Stack>
  );
}
